import React, { useState } from 'react';
import RoadmapItemEditor from './RoadmapItemEditor';
import { VALID_ROADMAP_STATUS } from '../utils/chartUtils';
import { sanitizeText, sanitizeDataItem } from '../utils/sanitization';

function RoadmapCreator({ onDataCreated, addingFirstItem, setAddingFirstItem }) {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [addingSubitemTo, setAddingSubitemTo] = useState(null);
  const [newItem, setNewItem] = useState({
    Nome: '',
    Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
    'Data de Inicio': '',
    'Previsão de Entrega': '',
    'Data de Conclusão': '',
    parentId: null,
    level: 0
  });

  const handleAddItem = (itemData) => {
    if (itemData.Nome.trim()) {
      // Sanitizar dados antes de adicionar
      const sanitizedData = sanitizeDataItem(itemData);
      
      // 🔧 MELHORIA: Geração de ID único mais robusta
      const generateUniqueId = () => {
        return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      };

      const itemWithConclusao = { 
        ...sanitizedData, 
        'Data de Conclusão': sanitizedData['Data de Conclusão'] || '', 
        id: generateUniqueId(),
        parentId: sanitizedData.parentId || newItem.parentId,
        level: sanitizedData.level || newItem.level
      };

      // 🐛 DEBUG: Log do item sendo criado
      if (process.env.NODE_ENV === 'development') {
        console.log('🆕 [DEBUG CREATOR] Criando item:', {
          nome: itemWithConclusao.Nome,
          id: itemWithConclusao.id,
          parentId: itemWithConclusao.parentId,
          level: itemWithConclusao.level,
          isSubitem: itemWithConclusao.level > 0,
          item: itemWithConclusao
        });
      }

      const updatedItems = [...items, itemWithConclusao];
      setItems(updatedItems);
      onDataCreated(updatedItems);
      setNewItem({
        Nome: '',
        Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
        'Data de Inicio': '',
        'Previsão de Entrega': '',
        'Data de Conclusão': '',
        parentId: null,
        level: 0
      });
      setAddingSubitemTo(null);
      setAddingFirstItem(false);
    }
  };

  const handleRemoveItem = (id) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    onDataCreated(updatedItems);
  };

    const handleEditItem = (itemData) => {
    // Sanitizar dados antes de editar
    const sanitizedData = sanitizeDataItem(itemData);
    const updatedItems = items.map(item => 
      item.id === sanitizedData.id ? sanitizedData : item
    );
    setItems(updatedItems);
    onDataCreated(updatedItems);
    setEditingId(null);
  };

  const handleAddSubitem = (parentId) => {
    setAddingSubitemTo(parentId);
    setNewItem({
      Nome: '',
      Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
      'Data de Inicio': '',
      'Previsão de Entrega': '',
      'Data de Conclusão': '',
      parentId: parentId,
      level: 1
    });
  };

  const handleCancelFirstItem = () => {
    setAddingFirstItem(false);
    setNewItem({
      Nome: '',
      Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
      'Data de Inicio': '',
      'Previsão de Entrega': '',
      'Data de Conclusão': '',
      parentId: null,
      level: 0
    });
  };

  const handleCancelSubitem = () => {
    setAddingSubitemTo(null);
    setNewItem({
      Nome: '',
      Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
      'Data de Inicio': '',
      'Previsão de Entrega': '',
      'Data de Conclusão': '',
      parentId: null,
      level: 0
    });
  };

  // Organizar itens por hierarquia
  const organizedItems = items.reduce((acc, item) => {
    if (item.level === 0 || !item.parentId) {
      // Item principal
      acc.push({ ...item, subitems: [] });
    } else {
      // Subitem - encontrar o pai
      const parentIndex = acc.findIndex(parent => parent.id === item.parentId);
      if (parentIndex !== -1) {
        acc[parentIndex].subitems.push(item);
      } else {
        // Se não encontrar o pai, tratar como item principal
        acc.push({ ...item, level: 0, parentId: null, subitems: [] });
      }
    }
    return acc;
  }, []);

  // Itens principais para seleção como pais
  const parentItems = items.filter(item => item.level === 0 || !item.parentId);

  return (
    <>
      {/* Formulário para adicionar primeiro item usando RoadmapItemEditor */}
      {items.length === 0 && addingFirstItem && (
        <div className="w-full max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-blue-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-blue-900">Primeiro Item do Roadmap</h4>
                  <p className="text-sm text-blue-700">Defina os detalhes do seu primeiro item</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <RoadmapItemEditor
                item={newItem}
                onSave={handleAddItem}
                onCancel={handleCancelFirstItem}
                isEditing={false}
                parentItems={[]}
                isSubitem={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Formulário para adicionar subitem */}
      {addingSubitemTo && (
        <div className="w-full max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-4 border-b border-amber-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-600 rounded-xl flex items-center justify-center shadow-sm">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-amber-900">Adicionar Subitem</h4>
                  <p className="text-sm text-amber-700">Adicione um item filho ao roadmap</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <RoadmapItemEditor
                item={newItem}
                onSave={handleAddItem}
                onCancel={handleCancelSubitem}
                isEditing={false}
                parentItems={parentItems}
                isSubitem={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Lista de itens criados com hierarquia */}
      {items.length > 0 && (
        <div className="w-full max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-5 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl flex items-center justify-center shadow-sm">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900">Itens do Roadmap</h4>
                  <p className="text-sm text-gray-600 font-medium">
                    {organizedItems.length} {organizedItems.length === 1 ? 'item principal' : 'itens principais'} • {items.filter(item => item.level === 1).length} subitens
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4 max-h-96 overflow-y-auto">
            {organizedItems.map((item) => (
              <div key={item.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                {/* Item principal */}
                <div className={`${item.subitems.length > 0 ? 'border-b border-gray-100' : ''}`}>
                  {editingId === item.id ? (
                    <div className="p-4">
                      <RoadmapItemEditor
                        item={item}
                        onSave={handleEditItem}
                        onCancel={() => setEditingId(null)}
                        isEditing={true}
                        parentItems={parentItems}
                        isSubitem={false}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-gray-900 text-base">{sanitizeText(item.Nome)}</span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.Status === 'Concluído' ? 'bg-green-100 text-green-800' :
                            item.Status === 'Em Progresso' ? 'bg-blue-100 text-blue-800' :
                            item.Status === 'Cancelado' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {sanitizeText(item.Status)}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                          <div>
                            {item['Data de Inicio'] && `Início: ${sanitizeText(item['Data de Inicio'])}`}
                          </div>
                          <div>
                            {item['Previsão de Entrega'] && `Previsão: ${sanitizeText(item['Previsão de Entrega'])}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleAddSubitem(item.id)}
                          className="p-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Adicionar subitem"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setEditingId(item.id)}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar item"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remover item"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Subitens */}
                {item.subitems.length > 0 && (
                  <div className="bg-gray-50">
                    {item.subitems.map((subitem) => (
                      <div key={subitem.id} className="border-b border-gray-100 last:border-b-0">
                        {editingId === subitem.id ? (
                          <div className="p-4 pl-8">
                            <RoadmapItemEditor
                              item={subitem}
                              onSave={handleEditItem}
                              onCancel={() => setEditingId(null)}
                              isEditing={true}
                              parentItems={parentItems}
                              isSubitem={true}
                            />
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-4 pl-8 hover:bg-gray-100 transition-colors">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="font-medium text-gray-800">{sanitizeText(subitem.Nome)}</span>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  subitem.Status === 'Concluído' ? 'bg-green-100 text-green-800' :
                                  subitem.Status === 'Em Progresso' ? 'bg-blue-100 text-blue-800' :
                                  subitem.Status === 'Cancelado' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {sanitizeText(subitem.Status)}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 ml-6">
                                <div>
                                  {subitem['Data de Inicio'] && `Início: ${sanitizeText(subitem['Data de Inicio'])}`}
                                </div>
                                <div>
                                  {subitem['Previsão de Entrega'] && `Previsão: ${sanitizeText(subitem['Previsão de Entrega'])}`}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => setEditingId(subitem.id)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Editar subitem"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleRemoveItem(subitem.id)}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remover subitem"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}export default RoadmapCreator;
